/**
 * The paragraph editor's behaviour (R18; spec 2026-10-23 §5 Tab 2): the key,
 * paste, typing and button handlers, the refusal and the live-region words,
 * and where focus goes after an edit. ParagraphListEditor.tsx only binds them.
 *
 * Kept in a .ts hook on purpose: the root jest cannot compile TSX, so the
 * events are proved here through react-test-renderer
 * (tests/unit/admin/app-experience-paragraph-editor-hook.test.ts).
 *
 * A READ-ONLY editor (`disabled`) does nothing: every handler returns first,
 * so Enter never splits and a paste never changes the text (fix round 1,
 * item 1). The edits themselves are pure functions in lib/paragraph-edits.ts.
 */
import { useEffect, useRef, useState } from 'react';
import { addAfter, insertText, move, removeAt, replaceText, splitAt, type EditResult } from './lib/paragraph-edits';

type FocusKind = 'text' | 'up' | 'down';

interface PendingFocus {
  /** Applied only when the editor shows exactly these paragraphs (the parent accepted the edit). */
  paragraphs: string[];
  index: number;
  caret: number;
  kind: FocusKind;
}

/** The parts of a React keydown that the editor reads. */
interface KeyLike {
  key: string;
  shiftKey: boolean;
  keyCode: number;
  nativeEvent: { isComposing: boolean };
  currentTarget: { selectionStart: number; selectionEnd: number };
  preventDefault: () => void;
}

/** The parts of a React paste that the editor reads. */
interface PasteLike {
  clipboardData: { getData: (type: string) => string };
  currentTarget: { selectionStart: number; selectionEnd: number };
  preventDefault: () => void;
}

const SHIFT_ENTER = 'A paragraph has no line breaks. Press Enter without Shift to start a new paragraph.';

export function useParagraphEditor({
  paragraphs,
  disabled,
  onChange,
}: {
  paragraphs: string[];
  disabled: boolean;
  onChange: (paragraphs: string[]) => void;
}) {
  const textRefs = useRef<(HTMLTextAreaElement | null)[]>([]);
  const upRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const downRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const pending = useRef<PendingFocus | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [said, setSaid] = useState('');

  useEffect(() => {
    const p = pending.current;
    if (!p || p.paragraphs !== paragraphs) return;
    pending.current = null;
    const last = paragraphs.length - 1;
    let kind = p.kind;
    if (kind === 'up' && p.index === 0) kind = last > 0 ? 'down' : 'text';
    if (kind === 'down' && p.index === last) kind = p.index > 0 ? 'up' : 'text';
    if (kind === 'up') upRefs.current[p.index]?.focus();
    else if (kind === 'down') downRefs.current[p.index]?.focus();
    else {
      const box = textRefs.current[p.index];
      box?.focus();
      box?.setSelectionRange(p.caret, p.caret);
    }
  }, [paragraphs]);

  /** Apply an edit: refuse in words, or change the text and say where focus goes. */
  const apply = (r: EditResult, kind: FocusKind, message?: string) => {
    if (!r.ok) {
      setRefusal(r.error);
      return;
    }
    setRefusal(null);
    if (r.focus) pending.current = { paragraphs: r.paragraphs, index: r.focus.index, caret: r.focus.caret, kind };
    if (message) setSaid(message);
    onChange(r.paragraphs);
  };

  const onKeyDown = (i: number) => (e: KeyLike) => {
    if (disabled) return;
    // A key that finishes an input-method composition (Chinese, Japanese, …) is not a split.
    if (e.key !== 'Enter' || e.nativeEvent.isComposing || e.keyCode === 229) return;
    e.preventDefault();
    if (e.shiftKey) {
      setRefusal(SHIFT_ENTER);
      return;
    }
    const box = e.currentTarget;
    apply(splitAt(paragraphs, i, box.selectionStart, box.selectionEnd), 'text', `New paragraph ${i + 2}.`);
  };

  const onPaste = (i: number) => (e: PasteLike) => {
    if (disabled) return;
    const box = e.currentTarget;
    const r = insertText(paragraphs, i, box.selectionStart, box.selectionEnd, e.clipboardData.getData('text/plain'));
    if (r === null) return; // one line: the browser pastes it as usual
    e.preventDefault();
    const added = r.ok ? r.paragraphs.length - paragraphs.length : 0;
    apply(r, 'text', added > 0 ? `The paste made ${added + 1} paragraphs.` : undefined);
  };

  const onText = (i: number, value: string) => {
    if (disabled) return;
    apply(replaceText(paragraphs, i, value), 'text');
  };

  // The buttons are disabled when read-only; the guard also holds for a keyboard shortcut or a test.
  const moveUp = (i: number) => {
    if (disabled) return;
    apply(move(paragraphs, i, -1), 'up', `Paragraph ${i + 1} moved up. It is now paragraph ${i}.`);
  };
  const moveDown = (i: number) => {
    if (disabled) return;
    apply(move(paragraphs, i, 1), 'down', `Paragraph ${i + 1} moved down. It is now paragraph ${i + 2}.`);
  };
  const addBelow = (i: number) => {
    if (disabled) return;
    apply(addAfter(paragraphs, i), 'text', `New paragraph ${i + 2}.`);
  };
  const remove = (i: number) => {
    if (disabled) return;
    apply(removeAt(paragraphs, i), 'text', `Paragraph ${i + 1} removed.`);
  };
  const addAtEnd = () => {
    if (disabled) return;
    apply(addAfter(paragraphs, paragraphs.length - 1), 'text', `New paragraph ${paragraphs.length + 1}.`);
  };

  return {
    refusal,
    said,
    textRef: (i: number) => (el: HTMLTextAreaElement | null) => {
      textRefs.current[i] = el;
    },
    upRef: (i: number) => (el: HTMLButtonElement | null) => {
      upRefs.current[i] = el;
    },
    downRef: (i: number) => (el: HTMLButtonElement | null) => {
      downRefs.current[i] = el;
    },
    onKeyDown,
    onPaste,
    onText,
    moveUp,
    moveDown,
    addBelow,
    remove,
    addAtEnd,
  };
}
